import { createAsyncThunk } from "@reduxjs/toolkit";
import { contracttParams } from "../types";
import DATAMART_SERVICES from "./DatamartServices";
import { datamartV3Services } from "./DatamartV3Services";

interface ContractApiResponse {
  status: any;
  message?: any;
  data?: any;
  total_count?: any;
  statusCode?: number;
  statusText?: string;
  requestId?: any;
}

// Define an async thunk for fetching Contract Summary
export const fetchSummary = createAsyncThunk(
  "contracts/fetchSummary",
  async (type: string, thunkAPI) => {
    try {
      const resp = (await DATAMART_SERVICES.fetchContractSummary(
        type,
      )) as ContractApiResponse;
      if (!resp?.["status"]) {
        return thunkAPI.rejectWithValue(
          resp?.["message"] || "Failed to fetch contract summary",
        );
      }
      return resp;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || "Unknown error");
    }
  },
);

// Define an async thunk for fetching Price List
export const fetchContracts = createAsyncThunk(
  "contracts/fetchContracts",
  async (params: contracttParams, thunkAPI) => {
    const { currentPage, itemsPerPage, sortOption, searchKeyword, type } =
      params;
    const apiParam = {
      limit: itemsPerPage,
      offset: (currentPage - 1) * itemsPerPage,
      order_by: sortOption,
      filter: searchKeyword || "",
    };
    try {
      const resp = (await DATAMART_SERVICES.fetchContractList(
        apiParam,
        type ?? "",
      )) as ContractApiResponse;
      if (!resp?.["status"]) {
        return thunkAPI.rejectWithValue(
          resp?.["message"] || "Failed to fetch contract list",
        );
      }
      return resp;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || "Unknown error");
    }
  },
);

// Define an async thunk for deleting a frame agreement
export const deleteFrameAgreement = createAsyncThunk(
  "contracts/deleteFrameAgreement",
  async (contractId: number, thunkAPI) => {
    try {
      const resp = (await DATAMART_SERVICES.deleteContract(
        contractId,
      )) as ContractApiResponse;
      if (!resp?.["status"]) {
        return thunkAPI.rejectWithValue(
          resp?.["message"] || "Failed to delete contract",
        );
      }
      return contractId;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || "Unknown error");
    }
  },
);

export const fetchContractCount = createAsyncThunk(
  "contracts/fetchCount",
  async (params: { filter: string; headers: any }) => {
    try {
      const response = (await DATAMART_SERVICES.fetchContractCount(
        params,
      )) as ContractApiResponse;
      if (response.status) {
        return response["data"]["total_count"];
      } else {
        throw new Error(
          response["message"] || "Failed to fetch contract count",
        );
      }
    } catch (error: any) {
      throw new Error(error.message || "Failed to fetch contract count");
    }
  },
);

// Load agreement rules from workflow configuration
export const fetchAgreementRules = createAsyncThunk(
  "contracts/fetchAgreementRules",
  async (_, thunkAPI) => {
    try {
      // Fetch both agreement rules and deviation rules in parallel
      const [agreementResult, deviationResult] = await Promise.all([
        DATAMART_SERVICES.getAgreementRules() as Promise<ContractApiResponse>,
        datamartV3Services.fetchContractDeviations() as Promise<ContractApiResponse>,
      ]);
      // Prepare the standard deviation data by mapping the API response to the expected structure
      const deviationData = deviationResult.data || [];
      return {deviationData, agreementRuleData: agreementResult.data};
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.message || "Failed to load agreement rules",
      );
    }
  },
);
